import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import ReactPlayer from "react-player";
import Typewriter from "../../CommonComponents/TypeWriting";
import Divider from "../../CommonComponents/Divider";
import PlayerOverlayButton from "../../CommonComponents/PlayerOverlayButton";
import { useSelector, useDispatch } from 'react-redux';
import { isStreamingStarted, loader } from '../../Services/Redux/Reducers/commonSlice';
// import { storeChat } from '../../Services/Redux/Reducers/chatSlice';
import { BrandingCardProps } from "../../Services/CommonServices/propTypes";
 
function BrandingCard(props) {
    const [isOverviewTextLoading, setIsOverviewTextLoading] = useState(false);
    const [isConclusionLoading, setIsConclusionLoading] = useState(false);
    const [brandingUrl, setBrandingUrl] = useState("");
    const [mediaType, setMediaType] = useState("");
    const [isInitialPlaying, setIsInitialPlaying] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [showControls, setShowControls] = useState(false);
    const [isBuffering, setIsBuffering] = useState(false);
    const hideTimer = useRef(null);
    const playerRef = useRef(null);
    const dispatch = useDispatch();
    const lastClickTime = useRef(0);
 
    // Memoized selectors to prevent unnecessary re-renders
    const conList = useSelector((state) => state.chat.conversationList);
    const loading = useSelector((state) => state.common.isLoader);
    const streamingState = useSelector((state) => state.chat.isStream);
    const isStopTriggered = useSelector((state) => state.chat.isStopButtonTriggered);
 
    // Memoize current index check
    const isLastConversation = useMemo(() =>
        props.index === conList.length - 1,
        [props.index, conList.length]
    );
 
    // Memoize text conditions
    const hasEmptyText = useMemo(() =>
        props.text === null || props.text === "",
        [props.text]
    );
 
    // Memoize complex loading conditions
    const shouldStoreChat = useMemo(() => {
        if (!isLastConversation || isStopTriggered || !props.isAnswerComplete || !loading) {
            return false;
        }
 
        const textCondition = hasEmptyText || (props.text != null && isOverviewTextLoading);
        const brandingCondition = !props.branding || props.branding.length === 0 || props.branding.length > 0;
        const conclusionCondition = !props.conclusion || props.conclusion === "" ||
            (props.conclusion && props.conclusion !== "" && isConclusionLoading);
 
        return textCondition && brandingCondition && conclusionCondition;
    }, [isLastConversation, isStopTriggered, props.isAnswerComplete, loading, hasEmptyText,
        props.text, isOverviewTextLoading, props.branding, props.conclusion, isConclusionLoading]);
 
    useEffect(() => {
        if (isLastConversation && loading && (props.text || props.conclusion)) {
            dispatch(isStreamingStarted(false));
        }
 
        if (shouldStoreChat) {
            // dispatch(storeChat(true));
            dispatch(loader(false));
        }
 
        if (!isStopTriggered && !isOverviewTextLoading && !isConclusionLoading && streamingState) {
            dispatch(loader(true));
        }
    }, [dispatch, isLastConversation, loading, props.text, props.conclusion, shouldStoreChat,
        isStopTriggered, isOverviewTextLoading, isConclusionLoading, streamingState]);
 
    // Optimized branding data processing
    useEffect(() => {
        if (!props.branding?.length) return;
 
        // Get the first media item (assuming single media per branding)
        const firstMedia = props.branding[0]?.media?.[0];
        if (firstMedia) {
            setMediaType(firstMedia.type);
            setBrandingUrl(firstMedia.url);
            // Reset video states when new video URL is set
            setIsPlaying(false);
            setIsInitialPlaying(false);
            setIsCompleted(false);
            setIsBuffering(false);
        }
    }, [props.branding]);
 
    // Auto-hide controls after 4 seconds when playing
    useEffect(() => {
        if (isPlaying) {
            setShowControls(true);
            clearTimeout(hideTimer.current);
            hideTimer.current = setTimeout(() => {
                setShowControls(false);
            }, 4000);
        }
        return () => clearTimeout(hideTimer.current);
    }, [isPlaying]);
 
    // Cleanup timer on unmount
    useEffect(() => {
        return () => clearTimeout(hideTimer.current);
    }, []);
 
    const handleVideoAreaClick = useCallback(() => {
        lastClickTime.current = Date.now();
        if (showControls) {
            // If controls are visible, hide them
            setShowControls(false);
            clearTimeout(hideTimer.current);
        } else {
            // If controls are hidden, show them and set 4-second auto-hide timer
            setShowControls(true);
            clearTimeout(hideTimer.current);
            hideTimer.current = setTimeout(() => {
                setShowControls(false);
            }, 4000);
        }
    }, [showControls]);
 
    const handlePlayPause = useCallback((e) => {
        e?.stopPropagation();
        setIsPlaying(prev => !prev);
        setIsCompleted(false);
        setIsInitialPlaying(true);
    }, []);
 
    const handleReplay = useCallback((e) => {
        e?.stopPropagation();
        setIsCompleted(false);
        setIsPlaying(true);
        setShowControls(true);
        setIsInitialPlaying(true);
    }, []);
 
    const handleVolume = useCallback((e) => {
        e?.stopPropagation();
        setIsMuted(prev => !prev);
    }, []);
 
    const handleFullScreen = useCallback((e) => {
        e?.stopPropagation();
        // For React Native WebView (mobile app)
        if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
            window.ReactNativeWebView.postMessage(JSON.stringify({ action: 'fullscreen', index: props.index }));
            return;
        }
 
        // Get video element directly for iOS handling
        const videoElement = document.getElementById(`player_container-${props.index}`);
        const container = document.getElementById(`player_container-${props.index}`);
        // Check if we're currently in fullscreen mode
        const isFullscreen = !!(
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement ||
            document.msFullscreenElement
        );
 
        // Exit fullscreen if already in fullscreen mode
        if (isFullscreen) {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.mozCancelFullScreen) {
                document.mozCancelFullScreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
            return;
        }
        // iOS Safari specific handling
        if (videoElement && /iPhone|iPad|iPod/i.test(navigator.userAgent)) {
            if (videoElement.webkitEnterFullscreen) {
                videoElement.webkitEnterFullscreen();
                return;
            } else if (videoElement.webkitRequestFullscreen) {
                videoElement.webkitRequestFullscreen();
                return;
            }
        }
        // Standard fullscreen API for other browsers
        if (container) {
            if (container.requestFullscreen) {
                container.requestFullscreen();
            } else if (container.webkitRequestFullscreen) {
                container.webkitRequestFullscreen();
            } else if (container.mozRequestFullScreen) {
                container.mozRequestFullScreen();
            } else if (container.msRequestFullscreen) {
                container.msRequestFullscreen();
            }
        }
    }, [props.index]);
 
    const handleVideoEnd = useCallback(() => {
        setIsPlaying(false);
        setIsCompleted(true);
        setIsInitialPlaying(false);
    }, []);
 
    // Memoized derived values to avoid unnecessary recalculations
    const showBrandingVideo = useMemo(() =>
        brandingUrl &&
        mediaType &&
        (!isLastConversation || hasEmptyText || isOverviewTextLoading),
        [brandingUrl, mediaType, isLastConversation, hasEmptyText, isOverviewTextLoading]
    );
 
    const showConclusion = useMemo(() =>
        props.conclusion &&
        props.conclusion !== "" &&
        (!isLastConversation || hasEmptyText || isOverviewTextLoading),
        [props.conclusion, isLastConversation, hasEmptyText, isOverviewTextLoading]
    );
 
    // Memoized icon sources
    const iconSources = useMemo(() => ({
        playPause: isPlaying && !isCompleted
            ? "./static/assets/videoPlayer_assets/pause.svg"
            : "./static/assets/videoPlayer_assets/play.svg",
        volume: isMuted
            ? "./static/assets/videoPlayer_assets/mute.svg"
            : "./static/assets/videoPlayer_assets/unmute.svg",
        replay: "./static/assets/videoPlayer_assets/replay.svg",
        maxMinScreen: "./static/assets/videoPlayer_assets/maxScreen.svg",
        thumbnail: "static/assets/videoPlayer_assets/Thumbnill.png",
        thumbnailPlay: "./static/assets/videoPlayer_assets/ThumbnillPlay.svg"
    }), [isPlaying, isCompleted, isMuted]);
 
    // Button configurations for reusability
    const buttonConfigs = useMemo(() => ({
        playPause: {
            position: { left: "12px", bottom: "12px" },
            onClick: handlePlayPause,
            iconSrc: iconSources.playPause,
            alt: "play-pause",
            zIndex: 1000
        },
        volume: {
            position: { right: "12px", bottom: "12px" },
            onClick: handleVolume,
            iconSrc: iconSources.volume,
            alt: "volume",
            zIndex: 1000
        },
        maxMinScreen: {
            position: { right: "12px", top: "12px" },
            onClick: handleFullScreen,
            iconSrc: iconSources.maxMinScreen,
            alt: "maxMinScreen",
            zIndex: 1000
        },
        replay: {
            position: { left: "12px", bottom: "12px" },
            onClick: handleReplay,
            iconSrc: iconSources.replay,
            alt: "replay",
            zIndex: 1000
        }
    }), [handlePlayPause, handleVolume, handleFullScreen, handleReplay, iconSources]);
 
    return (
        <div>
            {(props.text || brandingUrl || props.conclusion) && (
                <div className="chat_options d-flex flex-column response_bg branding-card" id={`player-${props.index}`}>
                    <div className="chat_msg1 col-md-12">
                        <div className="chat2">
                            <div className="chat2 col-lg-9">
                                <Typewriter
                                    convoId={props.convoId}
                                    type="responseText"
                                    text={props.text}
                                    delay={props.delay}
                                    isAnswerComplete={props.isAnswerComplete}
                                    setLoading={setIsOverviewTextLoading}
                                    effects={props.effects}
                                />
                            </div>
                        </div>
                        {(props.text === "" || isOverviewTextLoading) && showBrandingVideo && (
                            <div id={`player_container-${props.index}`} className="col-lg-7 branding-video-div">
                                {mediaType === "Video" ? (
                                    <div className="player-container" style={{ position: "relative", width: "100%", height: "600px", overflow: "hidden" }} >
                                        {/* Thumbnail layer */}
                                        {!isInitialPlaying && !isPlaying && (
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: "100%",
                                                    height: "100%",
                                                    zIndex: 5,
                                                }}
                                            >
                                                <img
                                                    src={iconSources.thumbnail}
                                                    className="thubnill_img"
                                                    alt="Video thumbnail"
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover",
                                                        display: "block"
                                                    }}
                                                />
                                                {!isCompleted && (
                                                    <div
                                                        className="play-thumbnill-overlay"
                                                        onClick={handlePlayPause}
                                                        style={{
                                                            position: "absolute",
                                                            top: "50%",
                                                            left: "50%",
                                                            transform: "translate(-50%, -50%)",
                                                            zIndex: 6,
                                                            cursor: "pointer"
                                                        }}
                                                    >
                                                        <img
                                                            src={iconSources.thumbnailPlay}
                                                            className="play-pause"
                                                            alt="ThumbnillPlay"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}
 
                                        {/* ReactPlayer layer */}
                                        <div
                                            className="video-player-container"
                                            onClick={handleVideoAreaClick}
                                            style={{
                                                position: "absolute",
                                                top: 0,
                                                left: 0,
                                                width: "100%",
                                                height: "100%",
 
                                                zIndex: (isInitialPlaying || isPlaying) ? 10 : 1,
                                                display: (isInitialPlaying || isPlaying) ? "block" : "none"
                                            }}
                                        >
                                            <ReactPlayer
                                                key={`player-${props.index}-${brandingUrl}`}
                                                ref={playerRef}
                                                url={brandingUrl}
                                                playing={isPlaying}
                                                muted={isMuted}
                                                width="100%"
                                                height="100%"
                                                className="branding-video-css"
                                                playsinline
                                                pip={true}
                                                onReady={() => {
                                                    console.log("#####video is ready before allowing interactions");
                                                    // Video is ready for interactions
                                                }}
                                                onLoadedData={() => {
                                                    console.log("#####Video data is loaded and ready to play");
                                                    // Video data is loaded and ready to play
                                                    setIsBuffering(false);
                                                }}
                                                onCanPlay={() => {
                                                    console.log("#####Video can start playing");
 
                                                    // Video can start playing
                                                    setIsBuffering(false);
                                                }}
                                                onBuffer={() => {
                                                    setIsBuffering(true);
                                                }}
                                                onBufferEnd={() => {
                                                    setIsBuffering(false);
                                                }}
                                                onEnded={handleVideoEnd}
                                                onError={(error) => {
                                                    console.warn('ReactPlayer error:', error);
                                                    setIsBuffering(false);
                                                }}
                                                config={{
                                                    file: {
                                                        attributes: {
                                                            id: `player-video-${props.index}`,
                                                            playsInline: true,
                                                            controls: false,
                                                            controlsList: 'nodownload',
                                                            disablePictureInPicture: false,
                                                            preload: 'auto',
                                                            poster: iconSources.thumbnail,
                                                            style: {
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: "fill",
                                                                display: "block"
 
                                                            }
                                                        },
                                                        forceVideo: true
                                                    }
                                                }}
                                            />
                                            <div className={`video-controls-overlay ${showControls ? 'visible' : ''}`}>
                                                <div className="controls-container">
                                                    <div style={{ pointerEvents: showControls ? "auto" : "none" }}>
                                                        <PlayerOverlayButton {...buttonConfigs.playPause} />
                                                    </div>
                                                    <div style={{ pointerEvents: showControls ? "auto" : "none" }}>
                                                        <PlayerOverlayButton {...buttonConfigs.volume} />
                                                    </div>
                                                    <div style={{ pointerEvents: showControls ? "auto" : "none" }}>
                                                        <PlayerOverlayButton {...buttonConfigs.maxMinScreen} />
                                                    </div> 
                                                </div>
                                            </div>
 
                                            {/* Loading indicator when buffering */}
                                            {isBuffering && (
                                                <div style={{
                                                    position: "absolute",
                                                    top: "50%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)",
                                                    zIndex: 1001,
                                                    color: "white",
                                                    fontSize: "14px",
 
                                                    padding: "8px 12px",
                                                    borderRadius: "4px"
                                                }}>
                                                    Loading...
                                                </div>
                                            )}
                                        </div>
 
                                        {isCompleted && (
                                            <div className="poster-image-overlay" style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 1000, pointerEvents: "none" }}>
                                                <div style={{ pointerEvents: "auto" }}>
                                                    <PlayerOverlayButton {...buttonConfigs.replay} />
                                                </div>
                                                <div style={{ pointerEvents: "auto" }}>
                                                    <PlayerOverlayButton {...buttonConfigs.volume} />
                                                </div>
                                                <div style={{ pointerEvents: "auto" }}>
                                                    <PlayerOverlayButton {...buttonConfigs.maxMinScreen} />
                                                </div> 
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div>
                                        <img
                                            src={brandingUrl}
                                            width="320px"
                                            style={{ aspectRatio: "auto" }}
                                            alt="branding"
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        <div>
                            {showConclusion && (
                                <div>
                                    <Divider borderTop="border-1" borderBottom="border-2" />
                                    <div className="col-lg-9 branding-mb">
                                        <Typewriter
                                            convoId={props.convoId}
                                            type="responseConclusion"
                                            text={props.conclusion ?? ""}
                                            delay={props.delay}
                                            isAnswerComplete={props.isAnswerComplete}
                                            setLoading={setIsConclusionLoading}
                                            effects={props.effects}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )
            }
        </div >
    );
}
 
BrandingCard.propTypes = BrandingCardProps;
 
export default BrandingCard;